import { X, Check, Crown, TrendingUp, Download, Copy, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
}

export default function PaywallModal({ isOpen, onClose, feature }: PaywallModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToPremium = () => {
    navigate('/signup');
    onClose();
  };

  const featureDetails: Record<string, { icon: any; color: string }> = {
    save: { icon: BarChart3, color: '#3b82f6' },
    pdf: { icon: Download, color: '#10b981' },
    csv: { icon: Download, color: '#8b5cf6' },
    audit: { icon: TrendingUp, color: '#f59e0b' },
    scenarios: { icon: Copy, color: '#ef4444' }
  };

  const currentFeature = featureDetails[feature] || { icon: Crown, color: '#3b82f6' };
  const FeatureIcon = currentFeature.icon;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          'radial-gradient(circle at top, rgba(24,82,214,0.18) 0%, rgba(0,0,0,0.72) 48%, rgba(0,0,0,0.82) 100%)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '0',
        backdropFilter: 'blur(16px) saturate(1.1)',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg2)',
          borderRadius: '24px',
          maxWidth: '520px',
          width: '100%',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.28)',
          border: '1px solid var(--border)',
          position: 'relative',
          overflow: 'hidden',
          margin: '60px 24px',
          maxHeight: 'calc(100vh - 120px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            appearance: 'none',
            background: 'rgba(255, 255, 255, 0.18)',
            border: 'none',
            cursor: 'pointer',
            color: 'white',
            padding: '8px',
            borderRadius: '8px',
            transition: 'all 0.15s ease',
            zIndex: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <X size={24} />
        </button>

        <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <div
            style={{
              background: `linear-gradient(135deg, ${currentFeature.color} 0%, ${currentFeature.color}dd 58%, #0f172a 140%)`,
              padding: '34px 24px 28px',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'white',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                marginBottom: '14px'
              }}
            >
              Alta Premium
              <span style={{ color: '#bbf7d0' }}>gratis</span>
            </div>
            <div
              style={{
                width: '64px',
                height: '64px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                border: '3px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <FeatureIcon size={32} color="white" />
            </div>

            <h2
              style={{
                margin: '0 0 8px',
                fontSize: '24px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              Crea tu cuenta gratis
            </h2>

            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.5',
              }}
            >
              Desbloquea exportaciones, escenarios y guardado ilimitado desde el primer momento.
            </p>
          </div>

          <div style={{ padding: '24px' }}>
          <div
            style={{
              marginBottom: '18px',
              padding: '16px 18px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(22,107,73,.08) 0%, rgba(24,82,214,.08) 100%)',
              border: '1px solid rgba(24,82,214,.10)',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text)', marginBottom: '4px' }}>
              Empiezas gratis
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.55' }}>
              Sin pago ahora, sin compromiso y con acceso inmediato a todas las funciones Premium.
            </div>
          </div>

          <h3
            style={{
              margin: '0 0 16px',
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text)',
            }}
          >
            Qué desbloqueas:
          </h3>

          <div
            style={{
              marginBottom: '18px',
              padding: '18px',
              borderRadius: '18px',
              background: 'linear-gradient(135deg, rgba(24,82,214,.09) 0%, rgba(22,107,73,.10) 100%)',
              border: '1px solid rgba(24,82,214,.12)',
              boxShadow: '0 10px 24px rgba(24,82,214,.08)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}
              >
                <BarChart3 size={20} />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text)', marginBottom: '3px' }}>
                  Guarda simulaciones ilimitadas
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.55' }}>
                  Recupera escenarios, compara cambios sin perder datos y vuelve a tus cálculos cuando quieras.
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {[
              'Guardar escenarios y simulaciones',
              'Detalle de IRPF (base, cuota total y anticipos)',
              'Deducciones por comunidad autónoma',
              'Proyección trimestral y análisis detallado',
              'Comparación de escenarios A/B',
              'Exportar informes PDF y CSV profesionales',
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Check size={14} color="#16a34a" strokeWidth={3} />
                </div>
                <span style={{ fontSize: '14px', color: 'var(--text)', lineHeight: '1.5', fontWeight: 600 }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={handleGoToPremium}
            style={{
              appearance: 'none',
              width: '100%',
              padding: '15px 24px',
              background: 'linear-gradient(135deg, #141210 0%, #1852d6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 800,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 10px 24px rgba(24, 82, 214, 0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 14px 30px rgba(24, 82, 214, 0.28)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 24px rgba(24, 82, 214, 0.22)';
            }}
          >
            <Crown size={18} />
            Crear cuenta gratis
          </button>

          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>
              Acceso inmediato · sin compromiso
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              appearance: 'none',
              width: '100%',
              padding: '10px 24px',
              backgroundColor: 'transparent',
              color: 'var(--muted)',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--muted)';
            }}
          >
            Continuar con versión gratuita
          </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
